<template>
  <div class="bg-white dark:bg-[#0c1427] rounded-xl shadow-sm border border-gray-100 dark:border-[#2a3441] overflow-hidden h-full flex flex-col">
    <!-- Header -->
    <div class="p-6 border-b border-gray-100 dark:border-[#2a3441]">
      <div class="flex items-start justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">{{ directT('chat.messages') }}</h2>
        <button 
          @click="showNewChatForm = true"
          class="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors" 
          style="margin-top: -0.5rem;"
        >
          <i class="material-symbols-outlined !text-base">add</i>
          {{ directT('chat.newChat') }}
        </button>
        </div>

      <!-- Search -->
      <div class="relative mb-4">
        <input 
          v-model="searchQuery"
          type="text" 
          :placeholder="directT('chat.search')" 
          class="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-[#2a3441] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white dark:bg-[#15203c] text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
        >
        <i class="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 !text-base">search</i>
          </div>

      <!-- Filters -->
      <div class="space-y-3">
        <!-- Status Filter Buttons -->
        <div class="flex w-full">
          <button 
            @click="statusFilter = ''"
            :class="{
              'flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors text-center': true,
              'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-300': statusFilter === '',
              'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#15203c]': statusFilter !== ''
            }"
          >
            {{ directT('chat.filters.allStatuses') }}
          </button>
          <div class="w-px bg-gray-200 dark:bg-gray-600 my-1"></div>
          <button 
            @click="statusFilter = 'open'"
            :class="{
              'flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors text-center': true,
              'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-300': statusFilter === 'open',
              'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#15203c]': statusFilter !== 'open'
            }"
          >
            {{ directT('chat.filters.open') }}
          </button>
          <div class="w-px bg-gray-200 dark:bg-gray-600 my-1"></div>
          <button 
            @click="statusFilter = 'closed'"
            :class="{
              'flex-1 py-1.5 text-xs rounded-lg font-medium transition-colors text-center': true,
              'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-300': statusFilter === 'closed',
              'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#15203c]': statusFilter !== 'closed'
            }"
          >
            {{ directT('chat.filters.closed') }}
          </button>
          </div>

        <!-- Dropdown Filters -->
        <div class="flex w-full">
            <select 
              v-model="assignmentFilter"
            class="flex-1 text-xs border border-gray-200 dark:border-[#2a3441] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-[#15203c] text-gray-900 dark:text-white appearance-none"
            style="background-image: none;"
            >
              <option value="">{{ directT('chat.filters.allAssignments') }}</option>
              <option value="assigned">{{ directT('chat.filters.assigned') }}</option>
              <option value="unassigned">{{ directT('chat.filters.unassigned') }}</option>
              <option value="my_assignments">{{ directT('chat.filters.assignedToMe') }}</option>
              <optgroup :label="directT('chat.filters.assignedToSpecificUser')" v-if="orgUsers && orgUsers.length > 0">
                <option 
                  v-for="user in (orgUsers || [])" 
                  :key="user.user_id" 
                  :value="`user_${user.user_id}`"
                >
                  {{ user.display_name }}
                </option>
              </optgroup>
            </select>
          <select 
            v-model="timeFilter"
            class="flex-1 text-xs border border-gray-200 dark:border-[#2a3441] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-[#15203c] text-gray-900 dark:text-white appearance-none ml-2"
            style="background-image: none;"
          >
            <option value="">{{ directT('chat.filters.allTimes') }}</option>
            <option value="1h">{{ directT('chat.filters.lastHour') }}</option>
            <option value="24h">{{ directT('chat.filters.last24Hours') }}</option>
            <option value="7d">{{ directT('chat.filters.lastWeek') }}</option>
            <option value="30d">{{ directT('chat.filters.lastMonth') }}</option>
            </select>
          </div>
        </div>
      </div>

    <!-- New Chat Form Modal -->
    <div 
      v-if="showNewChatForm" 
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeNewChatForm"
    >
      <div class="bg-white dark:bg-[#0c1427] rounded-xl shadow-xl border border-gray-200 dark:border-[#2a3441] w-full max-w-md mx-4">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-800 dark:text-white">{{ directT('chat.newChat') }}</h3>
            <button 
              @click="closeNewChatForm"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <i class="material-symbols-outlined">close</i>
            </button>
          </div>
          
          <form @submit.prevent="startNewChat" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ directT('chat.newChatForm.name') }}
        </label>
        <input
                v-model="newChatForm.name"
          type="text"
                :placeholder="directT('chat.newChatForm.namePlaceholder')"
                class="w-full px-3 py-2 border border-gray-300 dark:border-[#2a3441] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-[#15203c] text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ directT('chat.newChatForm.phone') }} *
              </label>
              <input
                v-model="newChatForm.phone"
                type="tel"
                required
                :placeholder="directT('chat.newChatForm.phonePlaceholder')"
                class="w-full px-3 py-2 border border-gray-300 dark:border-[#2a3441] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-[#15203c] text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ directT('chat.newChatForm.message') }} *
              </label>
              <textarea
                v-model="newChatForm.message"
                required
                rows="3"
                :placeholder="directT('chat.newChatForm.messagePlaceholder')"
                class="w-full px-3 py-2 border border-gray-300 dark:border-[#2a3441] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-[#15203c] text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 resize-none"
              ></textarea>
            </div>
            
            <div class="flex gap-3 pt-2">
              <button
                type="button"
                @click="closeNewChatForm"
                class="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#15203c] hover:bg-gray-200 dark:hover:bg-[#1a2332] rounded-lg transition-colors"
              >
                {{ directT('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="isStartingChat || !newChatForm.phone || !newChatForm.message"
                class="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <i v-if="isStartingChat" class="material-symbols-outlined animate-spin !text-base">refresh</i>
                {{ isStartingChat ? directT('chat.newChatForm.starting') : directT('chat.newChatForm.start') }}
              </button>
            </div>
      </form>
        </div>
      </div>
    </div>

    <!-- Chat List -->
    <div class="overflow-y-auto flex-1">
          <div
            v-for="(chat, index) in filteredChatList"
            :key="index"
        class="chat-item p-3 border-b border-gray-100 dark:border-[#2a3441] hover:bg-gray-50 dark:hover:bg-[#15203c] cursor-pointer transition-colors min-h-[60px]"
        :class="{ 'bg-primary-50 dark:bg-primary-900/20': selectedChatId === chat.id }"
            @click="selectChat(chat)"
          >
        <div class="flex items-start justify-between">
          <div class="flex items-start gap-2 flex-1">
            <div class="relative">
                <img
                  :src="chat.profile_picture_url || defaultProfileImage"
                  :alt="chat.name || chat.phone"
                class="h-8 w-8 rounded-full"
                  @error="handleImageError"
                />
                <span
                class="absolute right-0 bottom-0 w-2 h-2 rounded-full border border-white dark:border-[#0c1427]"
                :class="{
                  'bg-success-500': chat.status === 'online',
                  'bg-orange-400': chat.status === 'away',
                  'bg-gray-400': chat.status !== 'online' && chat.status !== 'away'
                }"
                ></span>
              </div>
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="font-medium text-sm text-gray-900 dark:text-white">{{ chat.name || chat.phone }}</h3>
                <span v-if="chat.assignedName" class="flex items-center gap-1" style="margin-top: -10px;">
                  <i class="material-symbols-outlined text-[10px] text-blue-500">person</i>
                  <span class="text-[9px] text-blue-600 dark:text-blue-400 font-medium">
                    {{ chat.assignedName }}
                  </span>
                  </span>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-300 truncate">
                {{ chat.typing ? directT('chat.typing') : chat.lastMessage }}
              </p>
            </div>
          </div>
          <div class="text-right flex flex-col items-end gap-1">
                  <span 
                    :class="{
                'px-1.5 py-0.5 text-xs rounded-full': true,
                'bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-300': isChatOpen(chat),
                'bg-gray-50 text-gray-600 dark:bg-gray-900/20 dark:text-gray-300': !isChatOpen(chat)
              }"
            >
              {{ isChatOpen(chat) ? directT('chat.filters.open') : directT('chat.filters.closed') }}
                  </span>
            <div class="flex items-center gap-1 relative">
              <p class="text-xs text-gray-400 dark:text-gray-500">{{ chat.lastTime }}</p>
              <!-- מונה הודעות שלא נקראו -->
              <span 
                v-if="chat.unread && chat.unread > 0"
                class="min-w-[16px] h-[16px] text-[8px] font-bold rounded-full flex items-center justify-center bg-red-500 text-white absolute -bottom-1 right-0"
              >
                {{ chat.unread > 99 ? "99+" : chat.unread }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import user31Image from "@/assets/images/users/user31.jpg";
import user6Image from "@/assets/images/users/user6.jpg";
import user7Image from "@/assets/images/users/user7.jpg";
import user8Image from "@/assets/images/users/user8.jpg";
import user9Image from "@/assets/images/users/user9.jpg";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { supabase } from "@/utils/supabase";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | string[] | TranslationObject;
};

// הגדרת ממשק לצ'אט
interface ChatData {
  id: string | number;
  name?: string;
  phone?: string;
  profile_picture_url?: string;
  organization_id?: string | number;
  image?: string;
  lastMessage?: string;
  lastTime?: string;
  unread?: number;
  assign_to?: string | null;
  assignedName?: string;
  isopen?: boolean;
  [key: string]: any; // לשדות נוספים שיכולים להיות
}

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Sidebar",
  emits: ['chat-selected', 'chats-loaded'],
  setup(props, { emit }) {
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject | string | string[] = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || typeof result === 'string' || Array.isArray(result) || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject | string | string[] = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || typeof enResult === 'string' || Array.isArray(enResult) || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject | string | string[];
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject | string | string[];
      }
      
      if (typeof result === 'string') {
        return result;
      } else if (Array.isArray(result)) {
        return result.join(', '); // Join array elements with comma
      } else {
        return key; // Return the key if result is not a string or array
      }
    };

    // Default profile image if none provided
    const defaultProfileImage = user31Image;
    
    // רשימת השיחות
    const chatList = ref<ChatData[]>([]);
    
    // פילטרים
    const statusFilter = ref<string>('');
    const timeFilter = ref<string>('');
    const assignmentFilter = ref<string>('');
    const searchQuery = ref<string>(''); // ← הוספת משתנה לחיפוש
    
    // רשימת משתמשים בארגון ומידע משתמש נוכחי
    const orgUsers = ref<any[]>([]);
    const currentUserId = ref<string>('');
    const selectedChatId = ref<string | number | null>(null);
    
    // New Chat Form
    const showNewChatForm = ref<boolean>(false);
    const isStartingChat = ref<boolean>(false);
    const newChatForm = ref({
      name: '',
      phone: '',
      message: ''
    });

    // Helper function to check if a chat is open/closed
    const isChatOpen = (chat: ChatData): boolean => {
      // צ'אט פתוח לפי העמודה isopen בטבלת threads
      return chat.isopen === true;
    };

    // Helper function to check if chat matches time filter
    const matchesTimeFilter = (chat: ChatData, timeFilter: string): boolean => {
      if (!timeFilter || !chat.lastTime) return true;
      
      // Parse lastTime (format like "1h", "2d", "just now", etc.)
      const lastTime = chat.lastTime.toLowerCase();
      
      if (lastTime === 'just now') return true;
      
      const value = parseInt(lastTime);
      const unit = lastTime.slice(-1);
      
      let hoursAgo = 0;
      if (unit === 'h') hoursAgo = value;
      else if (unit === 'd') hoursAgo = value * 24;
      else if (unit === 'm') hoursAgo = value / 60;
      
      switch (timeFilter) {
        case '1h':
          return hoursAgo <= 1;
        case '24h':
          return hoursAgo <= 24;
        case '7d':
          return hoursAgo <= 24 * 7;
        case '30d':
          return hoursAgo <= 24 * 30;
        default:
          return true;
      }
    };

    // רשימת הצ'אטים המסוננת
    const filteredChatList = computed(() => {
      return chatList.value.filter(chat => {
        // פילטר חיפוש טקסט
        if (searchQuery.value) {
          const query = searchQuery.value.toLowerCase();
          const nameMatch = (chat.name || '').toLowerCase().includes(query);
          const phoneMatch = (chat.phone || '').toLowerCase().includes(query);
          const messageMatch = (chat.lastMessage || '').toLowerCase().includes(query);
          
          if (!nameMatch && !phoneMatch && !messageMatch) {
            return false;
          }
        }

        // פילטר סטטוס (פתוח/סגור)
        if (statusFilter.value) {
          const isOpen = isChatOpen(chat);
          if (statusFilter.value === 'open' && !isOpen) return false;
          if (statusFilter.value === 'closed' && isOpen) return false;
        }

        // פילטר זמן
        if (timeFilter.value && !matchesTimeFilter(chat, timeFilter.value)) {
          return false;
        }

        // פילטר שיוך
        if (assignmentFilter.value) {
          if (assignmentFilter.value === 'assigned' && !chat.assign_to) return false;
          if (assignmentFilter.value === 'unassigned' && chat.assign_to) return false;
          if (assignmentFilter.value === 'my_assignments' && chat.assign_to !== currentUserId.value) return false;
          if (assignmentFilter.value.startsWith('user_') && chat.assign_to !== assignmentFilter.value.replace('user_', '')) return false;
        }

        return true;
      });
    });
    
    // פונקציה לטעינת הצ'אטים מהשרת
    const fetchChatThreads = async () => {
      console.log('fetchChatThreads: Starting to fetch chat threads');
      try {
        // Get user session to check if logged in
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        console.log('fetchChatThreads: Session check', { 
          isLoggedIn: !!session, 
          userId: session?.user?.id 
        });
        
        if (!session) {
          console.error('No active session');
          return;
        }
        
        // Store current user ID
        currentUserId.value = session.user.id;
        
        // First, get the user's profile to get their organization_id
        console.log('fetchChatThreads: Getting user profile');
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
          
        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          return;
        }
        
        if (!profileData || !profileData.org_id) {
          console.error('User profile has no organization_id');
          return;
        }
        
        console.log('fetchChatThreads: Got user profile', { 
          org_id: profileData.org_id 
        });
        
        // Load organization users for assignment filter
        await loadOrgUsers(profileData.org_id as string);
        
        // Use the new stored function to get threads with their last message in one query
        console.log('fetchChatThreads: Calling get_threads_with_last_message function');
        const { data, error } = await supabase
          .rpc('get_threads_with_last_message', { org_id: profileData.org_id });
          
        if (error) {
          console.error('Error fetching threads with last messages:', error);
          return;
        }
        
        console.log('fetchChatThreads: Received data from function', { 
          count: (data as any[])?.length, 
          firstThread: data && (data as any[]).length > 0 ? (data as any[])[0] : null,
          nameField: data && (data as any[]).length > 0 ? (data as any[])[0].name : 'no name field'
        });
        
        // Create thread list from the results
        chatList.value = data ? (data as any[]).map((thread: any) => ({
          id: thread.thread_id,
          name: thread.name || thread.phone,
          phone: thread.phone,
          profile_picture_url: thread.profile_picture_url,
          organization_id: thread.organization_id,
          image: thread.profile_picture_url || defaultProfileImage,
          lastMessage: formatLastMessage(thread.last_message, thread.last_message_type),
          lastTime: formatTimestamp(thread.last_message_time || thread.created_at),
          unread: thread.unread_count || 0,
          status: thread.status || 'offline',
          botstatus: thread.botstatus,
          assign_to: thread.assign_to,
          isopen: thread.isopen,
          assignedName: undefined // Will be loaded separately
        })) : [];
        
        console.log('fetchChatThreads: Mapped chat data', { 
          chatListCount: chatList.value.length,
          firstChat: chatList.value.length > 0 ? chatList.value[0] : null
        });

        // Load assigned user names for chats that have assign_to
        await loadAssignedUserNames();

        // Emit the chats-loaded event with the chat list
        if (chatList.value.length > 0) {
          emit('chats-loaded', chatList.value);
        }
        
      } catch (error) {
        console.error('Failed to fetch chat threads:', error);
        
        // Fallback to demo data if fetching fails
        console.log('fetchChatThreads: Using fallback demo data');
        chatList.value = [
          {
            id: 1,
            name: "Sarah Smith",
            image: user31Image,
            lastMessage: directT('chat.typing'),
            typing: true,
            lastTime: "1h",
            unread: 0,
            botstatus: true,
            assign_to: null,
            isopen: true,
            is_customer: false
          },
          {
            id: 2,
            name: "Michael Johnson",
            image: user6Image,
            lastMessage: directT('chat.attachment'),
            typing: false,
            lastTime: "2h",
            unread: 3,
            botstatus: false,
            assign_to: null,
            isopen: false,
            is_customer: true
          },
          {
            id: 3,
            name: "Emily Brown",
            image: user7Image,
            lastMessage: directT('chat.voiceMessage'),
            typing: false,
            lastTime: "4h",
            unread: 0,
            botstatus: true,
            assign_to: null,
            isopen: true,
            is_customer: false
          },
          {
            id: 4,
            name: "David Martinez",
            image: user8Image,
            lastMessage: "Excellent work...",
            typing: false,
            lastTime: "8h",
            unread: 0,
            botstatus: false,
            assign_to: currentUserId.value,
            isopen: true,
            is_customer: true
          },
          {
            id: 5,
            name: "Jessica Taylor",
            image: user9Image,
            lastMessage: "Hello! Mr.. 🔥",
            typing: false,
            lastTime: "1d",
            unread: 2,
            botstatus: true,
            assign_to: null,
            isopen: false,
            is_customer: false
          }
        ];
        
        // Emit the chats-loaded event with the fallback chat list
        emit('chats-loaded', chatList.value);
      }
    };
    
    // Helper function to format last message based on its type
    const formatLastMessage = (message: string | null, messageType: string | null): string => {
      // Check message type first before checking if message is null
      if (messageType === 'image' || messageType === 'photo') {
        return '📷 ' + directT('chat.photo');
      } else if (messageType === 'audio' || messageType === 'voice') {
        return '🎤 ' + directT('chat.voiceMessage');
      } else if (messageType === 'file' || messageType === 'document') {
        return '📎 ' + directT('chat.attachment');
      } else {
        // For text messages, check if we have content
        if (!message) return directT('chat.noMessages');
        
        // Regular text message - limit to first few words
        return message.length > 30 
          ? message.substring(0, 27) + '...' 
          : message;
      }
    };
    
    // Format timestamp to relative time (e.g., "2h ago")
    const formatTimestamp = (timestamp: string): string => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMin = Math.floor(diffInMs / (1000 * 60));
      
      if (diffInMin < 1) return directT('chat.timeFormat.justNow');
      if (diffInMin < 60) return directT('chat.timeFormat.minutesAgo').replace('{count}', diffInMin.toString());
      
      const diffInHours = Math.floor(diffInMin / 60);
      if (diffInHours < 24) return directT('chat.timeFormat.hoursAgo').replace('{count}', diffInHours.toString());
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return directT('chat.timeFormat.daysAgo').replace('{count}', diffInDays.toString());
      
      if (diffInDays < 30) {
        const diffInWeeks = Math.floor(diffInDays / 7);
        return directT('chat.timeFormat.weeksAgo').replace('{count}', diffInWeeks.toString());
      }
      
      return date.toLocaleDateString();
    };

    // Function to load organization users for assignment filter
    const loadOrgUsers = async (orgId: string) => {
      try {
        console.log('Loading organization users for org:', orgId);

        const { data: users, error } = await supabase
          .from('user_profiles')
          .select('user_id, display_name, email')
          .eq('org_id', orgId)
          .order('display_name');

        if (error) {
          console.error('Error fetching organization users:', error);
          return;
        }

        orgUsers.value = users || [];
        console.log('Loaded organization users:', orgUsers.value);
      } catch (error) {
        console.error('Error loading organization users:', error);
      }
    };

    // Function to load assigned user names
    const loadAssignedUserNames = async () => {
      try {
        // Get unique user IDs that are assigned
        const assignedUserIds = [...new Set(
          chatList.value
            .filter(chat => chat.assign_to)
            .map(chat => chat.assign_to)
        )];

        if (assignedUserIds.length === 0) return;

        console.log('Loading assigned user names for IDs:', assignedUserIds);

        // Use orgUsers if already loaded, otherwise fetch from database
        if (orgUsers.value && orgUsers.value.length > 0) {
          // Create a map of user_id to display_name from orgUsers
          const userMap = new Map();
          orgUsers.value.forEach(user => {
            userMap.set(user.user_id, user.display_name);
          });

          // Update chat list with assigned names
          chatList.value.forEach(chat => {
            if (chat.assign_to && userMap.has(chat.assign_to)) {
              chat.assignedName = userMap.get(chat.assign_to);
            }
          });
        } else {
          // Fallback to database query
          const { data: users, error } = await supabase
            .from('user_profiles')
            .select('user_id, display_name')
            .in('user_id', assignedUserIds);

          if (error) {
            console.error('Error fetching assigned user names:', error);
            return;
          }

          // Create a map of user_id to display_name
          const userMap = new Map();
          users?.forEach(user => {
            userMap.set(user.user_id, user.display_name);
          });

          // Update chat list with assigned names
          chatList.value.forEach(chat => {
            if (chat.assign_to && userMap.has(chat.assign_to)) {
              chat.assignedName = userMap.get(chat.assign_to);
            }
          });
        }

        console.log('Updated chat list with assigned names');
      } catch (error) {
        console.error('Error loading assigned user names:', error);
      }
    };

    // בחירת צ'אט
    const selectChat = async (chat: ChatData): Promise<void> => {
      console.log('Sidebar: Chat selected', { 
        id: chat.id, 
        name: chat.name, 
        phone: chat.phone, 
        organization_id: chat.organization_id 
      });
      
      // Update selected chat ID for visual highlighting
      selectedChatId.value = chat.id;
      
      // Mark all messages as read for this chat
      try {
        const { error } = await supabase
          .from('messages')
          .update({ read: true })
          .eq('phone', chat.phone)
          .eq('organization_id', chat.organization_id)
          .eq('read', false);  // רק הודעות שעדיין לא נקראו
          
        if (error) {
          console.error('Error marking messages as read:', error);
        } else {
          console.log('✅ Marked all messages as read for chat:', chat.name || chat.phone);
        }
      } catch (error) {
        console.error('Error updating read status:', error);
      }
      
      // Reset unread count for selected chat in UI
      const chatIndex = chatList.value.findIndex(c => c.id === chat.id);
      if (chatIndex !== -1) {
        chatList.value[chatIndex].unread = 0;
      }
      
      emit('chat-selected', chat);
    };

    // טיפול בשגיאות טעינת תמונות
    const handleImageError = (event: Event): void => {
      const target = event.target as HTMLImageElement;
      if (target) {
        target.src = defaultProfileImage;
      }
    };
    
    // טיפול בשגיאות טעינת תמונות של בוטים
    const handleBotImageError = (event: Event): void => {
      const target = event.target as HTMLImageElement;
      if (target) {
        target.src = defaultProfileImage;
      }
    };
    
    // Function to update assigned name for a specific chat
    const updateChatAssignment = (chatId: string | number, assignedUserId: string | null, assignedUserName: string) => {
      const chatIndex = chatList.value.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        chatList.value[chatIndex].assign_to = assignedUserId;
        chatList.value[chatIndex].assignedName = assignedUserName;
        console.log(`Updated assignment for chat ${chatId}:`, { assignedUserId, assignedUserName });
      }
    };

    // Expose function to parent component
    const refreshChatList = () => {
      fetchChatThreads();
    };

    // Function to update chat with new message
    const updateChatWithNewMessage = (data: { message: any, phone: string, threadId: string | null }) => {
      console.log('📨 Updating chat with new message:', data);
      console.log('📝 Message content:', data.message?.text);
      console.log('📱 Phone number:', data.phone);
      console.log('🆔 Thread ID:', data.threadId);
      console.log('👤 From customer:', data.message?.from_our_customer);
      
      // Find the chat in the list by phone number or thread ID
      let chatIndex = -1;
      
      if (data.phone) {
        chatIndex = chatList.value.findIndex(chat => chat.phone === data.phone);
        console.log('🔍 Searching by phone, found index:', chatIndex);
      } else if (data.threadId) {
        chatIndex = chatList.value.findIndex(chat => chat.id.toString() === data.threadId);
        console.log('🔍 Searching by threadId, found index:', chatIndex);
      }
      
      if (chatIndex !== -1) {
        // Update the chat with new message info
        const updatedChat = { ...chatList.value[chatIndex] };
        
        console.log('📋 Current chat before update:', {
          name: updatedChat.name,
          lastMessage: updatedChat.lastMessage,
          lastTime: updatedChat.lastTime,
          unread: updatedChat.unread
        });
        
        // Format the last message based on type
        const formattedMessage = formatLastMessage(data.message.text, data.message.type);
        const formattedTime = formatTimestamp(data.message.created_at);
        
        updatedChat.lastMessage = formattedMessage;
        updatedChat.lastTime = formattedTime;
        
        // Increment unread count only for incoming messages (from customers)
        // If it's an incoming message and the chat is NOT currently selected, increment unread count
        if (!data.message.from_our_customer) {
          // Check if this chat is currently selected (if so, messages are automatically read)
          const isCurrentlySelectedChat = selectedChatId.value && selectedChatId.value.toString() === updatedChat.id.toString();
          
          if (!isCurrentlySelectedChat) {
          updatedChat.unread = (updatedChat.unread || 0) + 1;
            console.log('📈 Incremented unread count to:', updatedChat.unread, 'for chat:', updatedChat.name || updatedChat.phone);
          } else {
            console.log('📖 Message is for currently selected chat, no unread increment');
          }
        } else {
          console.log('📤 Message is outgoing, no unread increment needed');
        }
        
        console.log('📋 Chat after update:', {
          name: updatedChat.name,
          lastMessage: updatedChat.lastMessage,
          lastTime: updatedChat.lastTime,
          unread: updatedChat.unread
        });
        
        // Remove chat from current position
        const updatedChatList = [...chatList.value];
        updatedChatList.splice(chatIndex, 1);
        
        // Add chat at the beginning of the list (most recent)
        updatedChatList.unshift(updatedChat);
        
        chatList.value = updatedChatList;
        
        console.log('✅ Successfully updated chat list with new message. Chat moved to top:', updatedChat.name || updatedChat.phone);
        console.log('🎯 Preview message updated to:', formattedMessage);
      } else {
        console.log('❌ Chat not found in list for phone:', data.phone, 'or threadId:', data.threadId);
        console.log('📋 Available chats:', chatList.value.map(chat => ({
          id: chat.id,
          name: chat.name,
          phone: chat.phone
        })));
        // Refresh the entire chat list if chat not found
        // This might happen for new chats
        console.log('🔄 Refreshing chat list to include new chat...');
        fetchChatThreads();
      }
    };

    // New Chat Form Functions
    const closeNewChatForm = () => {
      showNewChatForm.value = false;
      newChatForm.value = {
        name: '',
        phone: '',
        message: ''
      };
    };

    const startNewChat = async () => {
      if (!newChatForm.value.phone || !newChatForm.value.message) {
        return;
      }

      isStartingChat.value = true;
      
      try {
        // Get user session to get organization_id
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (!session) {
          console.error('No active session');
          return;
        }
        
        // Get user profile to get organization_id
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', session.user.id)
          .single();
          
        if (profileError || !profileData?.org_id) {
          console.error('Error fetching user profile or no organization:', profileError);
          return;
        }

        // Prepare API request
        const requestBody = {
          organization_id: profileData.org_id,
          phone: newChatForm.value.phone,
          message: newChatForm.value.message,
          ...(newChatForm.value.name && { name: newChatForm.value.name })
        };

        console.log('Starting new chat with:', requestBody);

        // Make API request
        const response = await fetch('https://api.clickai.live/start-new-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json();
        
        if (response.ok && result.success) {
          console.log('New chat started successfully:', result);
          
          // Close the form
          closeNewChatForm();
          
          // Refresh chat list to show the new chat
          await fetchChatThreads();
          
          // Find and select the new chat by thread_id
          setTimeout(() => {
            const newChat = chatList.value.find(chat => 
              chat.id.toString() === result.thread_id.toString()
            );
            
            if (newChat) {
              console.log('Found new chat, selecting it:', newChat);
              selectChat(newChat);
            } else {
              console.log('New chat not found in list, searching by phone:', result.formatted_phone);
              // Fallback: try to find by formatted phone number
              const chatByPhone = chatList.value.find(chat => 
                chat.phone === result.formatted_phone || 
                chat.phone?.replace(/\D/g, '').includes(result.formatted_phone.replace(/\D/g, ''))
              );
              
              if (chatByPhone) {
                console.log('Found chat by phone, selecting it:', chatByPhone);
                selectChat(chatByPhone);
              } else {
                console.log('Could not find new chat in list');
              }
            }
          }, 1000); // Wait 1 second for chat list to update
          
          // Show success message (you can implement a toast notification here)
          console.log('Chat started successfully!');
          
        } else {
          console.error('Failed to start new chat:', result);
          // Show error message (you can implement a toast notification here)
        }

      } catch (error) {
        console.error('Error starting new chat:', error);
        // Show error message (you can implement a toast notification here)
      } finally {
        isStartingChat.value = false;
      }
    };

    // Load chat threads when component mounts
    onMounted(() => {
      fetchChatThreads();
    });

    return {
      chatList,
      filteredChatList,
      selectChat,
      directT,
      defaultProfileImage,
      statusFilter,
      timeFilter,
      assignmentFilter,
      searchQuery,
      orgUsers,
      currentUserId,
      selectedChatId,
      isChatOpen,
      handleImageError,
      updateChatAssignment,
      refreshChatList,
      updateChatWithNewMessage,
      // New Chat Form
      showNewChatForm,
      isStartingChat,
      newChatForm,
      closeNewChatForm,
      startNewChat
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar for chat list */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: #374151;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}

/* Mobile responsive adjustments */
@media (max-width: 1024px) {
  .lg\:grid {
    display: block !important;
  }
}
</style>
